import React, { useState, useRef, useMemo, useEffect } from 'react';
import { DropdownProps } from 'semantic-ui-react';
import _ from 'lodash';
import { Link } from 'react-router-dom';
import { searchSpecialistMeetingRequestList , getSpecialistMeetingListAssignedToMaster} from '../../../Services/SpecialistMeetingServices';
import { AxiosResponse } from 'axios';
import { utils } from '../../../helpers/utils';
import AppConfig from '../../../Constants/config';
import moment from 'moment';
import { User } from '../../../Types/User';
import { userRole } from "../../../Constants/userRoles";

export interface PageSizeOption {
    key: number;
    value: string | number;
    text: string;
}

export interface GetSMRPayload {
    sortByField: string | null;
    sortOrderDesc: boolean;
    searchText: string | null;
    recordsPerPage: number;
    pageNum: number;
    isPending: boolean;
    assignedTo: number;
}

export interface GetSMRDataRow {
    [key: string]: any;
    meetingRequestId: number;
    requestedBy: string | undefined;
    requestedOn: string | undefined;
    helpTopics: string[];
    assignedTo: string | undefined;
    meetingSchedule: string | undefined;
}

export interface GetSMRData {
    totalRecords: number;
    gridData: GetSMRDataRow[];
}

export interface MasterDataItem {
    value: string;
    id: number;
}

export interface MasterData {
    assignedToOptions: PageSizeOption[],
}

export interface MasterDataResponse {
    
}

export interface ColumnDefinition {
    columnName: string;
    sortable: boolean;
    columnHeader: string;
    decorator?: Function;
}

export function useRSASpecialistMeetingRequestList(saveState: boolean = true, statePropName?: string) {
    const currUser: User | undefined= utils.getUserFromLocalToken() as (User | undefined);
    const pageSizeOptions = useMemo<PageSizeOption[]>(() => {
        return [
            { key: 3, value: 3, text: '3' },
            { key: 5, value: 5, text: '5' },
            { key: 10, value: 10, text: '10' },
            { key: 15, value: 15, text: '15' },
            { key: 20, value: 20, text: '20' },
            { key: 50, value: 50, text: '50' },
            { key: 100, value: 100, text: '100' }
        ];
    }, []);

    const searchRef = useRef<HTMLInputElement>(null);
    const [masterData, setMasterData] = useState<MasterData>();
    const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [pageNum, setPageNum] = useState<number>(1);
    const [sortByField, setSortByField] = useState<string | null>(null);
    const [sortOrderDesc, setSortOrderDesc] = useState<boolean>(true);
    const [searchText, setSearchText] = useState<string | null>(null);
    const [data, setData] = useState<GetSMRDataRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentUserId, setCurrentUserId] = useState<string>((currUser && currUser.UserId) ? currUser.UserId : "0");
    const [isUserCoordinator, setIsUserCoordinator] = useState<boolean>((currUser?.Roles?.includes(userRole['Employment Coordinator']) === true) ? true : false);

    const [isPending, setIsPending] = useState<boolean>(true);
    const [assignedTo, setAssignedTo] = useState<number>(0);

    const handlePageSizeChange: ((event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => void) | undefined = (event, data) => {
        setRecordsPerPage(data.value as number);
        const payload: GetSMRPayload = {
            recordsPerPage: data.value as number,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            isPending: isPending,
            assignedTo: assignedTo,
        };
        _fetchData(payload);
    }

    const handlePaginationChange: (newPageNumber: number) => void = (newPageNumber) => {
        setPageNum(newPageNumber);
        const payload: GetSMRPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: newPageNumber,
            isPending: isPending,
            assignedTo: assignedTo,
        };
        _fetchData(payload);
    }

    const handleIsPendingChange: (newValue: boolean) => void = (newValue) => {
        setIsPending(newValue);
        setAssignedTo(0);
        setPageNum(1);
        const payload: GetSMRPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: 1,
            isPending: newValue,
            assignedTo: 0,
        };
        _fetchData(payload);
    }

    const handleAssignedToChange: (newValue: number) => void = (newValue) => {
        setAssignedTo(newValue);
        setPageNum(1);
        const payload: GetSMRPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: 1,
            isPending: isPending,
            assignedTo: newValue
        };
        _fetchData(payload);
    }
    
    const handleSortChange: (newSortColumnName: string) => void = (newSortColumnName) => {
        let newIsDesc = sortOrderDesc;
        let newColumnName = sortByField;
        if (newSortColumnName === sortByField) {
            newIsDesc = !newIsDesc;
        } else {
            newColumnName = newSortColumnName;
        }

        setSortOrderDesc(newIsDesc);
        setSortByField(newColumnName);
        setPageNum(1);
        const payload: GetSMRPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: newColumnName,
            sortOrderDesc: newIsDesc,
            searchText: searchText,
            pageNum: 1,
            isPending: isPending,
            assignedTo: assignedTo,
        };
        _fetchData(payload);
    }

    const handleSearchChangeFn: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        //setSearchText((event.target as HTMLInputElement).value);
        setSearchText((event.target as HTMLInputElement).value);
        setPageNum(1);
        const payload: GetSMRPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: (event.target as HTMLInputElement).value,
            pageNum: 1,
            isPending: isPending,
            assignedTo: assignedTo,
        };
        _fetchData(payload);
    }
    const handleSearchChange = _.debounce(handleSearchChangeFn, 1000);

    const resetSearch: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        if (searchRef.current !== null) {
            (searchRef.current as HTMLInputElement).value = '';
        }
        setSearchText(null);
        setPageNum(1);
        const payload: GetSMRPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: null,
            pageNum: 1,
            isPending: isPending,
            assignedTo: assignedTo,
        };
        _fetchData(payload);
    }

    const resetFilter: (event: React.SyntheticEvent<HTMLElement, Event>) => void = (event) => {
        setSearchText(null);
        if(searchRef.current){
            (searchRef.current as HTMLInputElement).value = '';
        }
        setPageNum(1);
        const payload: GetSMRPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: null,
            pageNum: 1,
            isPending: isPending,
            assignedTo: assignedTo,
        };
        _fetchData(payload);
    }


    const _fetchData: (overwritePayload?: GetSMRPayload) => void = (overwritePayload) => {
        const defaultPayload: GetSMRPayload = {
            recordsPerPage: recordsPerPage,
            sortByField: sortByField,
            sortOrderDesc: sortOrderDesc,
            searchText: searchText,
            pageNum: pageNum,
            isPending: isPending,
            assignedTo: 0,
        };

        let payload: GetSMRPayload = defaultPayload;
        if (overwritePayload !== undefined) {
            payload = overwritePayload;
        }
        
        setIsLoading(true);
        if (saveState) {
            _saveState(payload);
        }
        searchSpecialistMeetingRequestList(payload, false).then((result) => {
            setIsLoading(false);

            let response: AxiosResponse<unknown, GetSMRData> = result as AxiosResponse;
            if (response.status === 200 && response.data) {

                let data = response.data as GetSMRData;
                //console.log(data);
                setData(data.gridData);
                setTotalRecords(data.totalRecords);
            }
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _fetchMasterData: (callback?: Function) => void = (callBack = () => { }) => {
        setIsLoading(true);
        getSpecialistMeetingListAssignedToMaster(false).then((result) => {
            setIsLoading(false);
            let response: AxiosResponse<unknown, MasterDataResponse> = result as AxiosResponse;
            if (response && response.status === 200 && response.data) {
                let data = response.data as MasterDataItem[];
                let newMasterData:MasterData = {
                    assignedToOptions:[]
                };
                
                let assignedToOptions: PageSizeOption[] = data.map((item,index)=>{
                    return {
                        key: index,
                        value: item.id,
                        text: item.value
                    }
                });
                assignedToOptions.unshift({key: -1,value:0,text:'Any'});
                newMasterData.assignedToOptions= assignedToOptions;
                setMasterData(newMasterData);
            }
            callBack();
        }, (error) => {
            setIsLoading(false);
            console.log(error);
        });
    }

    const _saveState = (currentPayload: GetSMRPayload) => {
        let stateName = AppConfig.RSA_SPECIALLIST_MEETING_REQUEST_LIST_STATE + "_id_" +currentUserId.toString();
        if (saveState) {
            utils.updateInLocalStorage(stateName, JSON.stringify(currentPayload));
        }
    }

    const _loadState = () => {
        let stateName = AppConfig.RSA_SPECIALLIST_MEETING_REQUEST_LIST_STATE + "_id_" + currentUserId.toString();
        let stateString: string | null = utils.getLocalStorageItem(stateName);
        if (stateString && saveState) {
            return JSON.parse(stateString) as GetSMRPayload;
        } else {
            return null;
        }

    }

    useEffect(() => {
        _fetchMasterData(() => {
            let priviousState: GetSMRPayload | null = _loadState();
            if (priviousState && saveState) {
                _fetchData(priviousState);
                setSortByField(priviousState.sortByField);
                setSortOrderDesc(priviousState.sortOrderDesc);
                setSearchText(priviousState.searchText);
                setRecordsPerPage(priviousState.recordsPerPage);
                setIsPending(priviousState.isPending);
                setAssignedTo(priviousState.assignedTo);
                setPageNum(priviousState.pageNum);
                if (searchRef.current) {
                    searchRef.current.value = priviousState.searchText || '';
                }
                
            } else {
                _fetchData();
            }
        });

    }, []);

    // column definition

    let columns: ColumnDefinition[];
    
    if(isPending){
        columns = [
            {
                columnName: 'clientName',
                sortable: false,
                columnHeader: 'Client name',
                decorator: (row: GetSMRDataRow, data: GetSMRDataRow[], rowIndex: number, colIndex: number ) => {
                    if(!!row.meetingSchedule && row.meetingSchedule.length > 0){
                        return (<Link to={`/RSASpecialistMeetingScheduler/${row.meetingRequestId || 0}`}>{row.clientName}</Link>);
                    }else {
                        return (<span >{row.clientName}</span>);
                    }
                    
                },
            },
            {
                columnName: 'requestedBy',
                sortable: false,
                columnHeader: 'Requested by',
                decorator:  (row: GetSMRDataRow, data: GetSMRDataRow[], rowIndex: number, colIndex: number ) => {
                    return (<span>{row.requestedBy ? row.requestedBy: 'Unknown'}</span>);
                },
            },
            {
                columnName: 'assignedTo',
                sortable: false,
                columnHeader: 'Employment coordinator',
                decorator:  (row: GetSMRDataRow, data: GetSMRDataRow[], rowIndex: number, colIndex: number ) => {
                    return (<span>{row.assignedTo ? row.assignedTo: 'Unassigned'}</span>);
                },
            },
            {
                columnName: 'requestedOn',
                sortable: false,
                columnHeader: 'Requested on',
                decorator: (row: GetSMRDataRow, data: GetSMRDataRow[], rowIndex: number, colIndex: number ) => {
                    return (<span>{moment(row.requestedOn).format('MM/DD/yyyy')}</span>);
                },
            },
            {
                columnName: 'helpTopics',
                sortable: false,
                columnHeader: 'Help topic',
                decorator: (row: GetSMRDataRow, data: GetSMRDataRow[], rowIndex: number, colIndex: number ) => {
                    let otherAssistnaceIndex = row.helpTopics.findIndex((item)=>{
                        return item.toLocaleLowerCase().includes('other');
                    });
                    if(otherAssistnaceIndex !== -1){
                        let otherAssistanceItem = row.helpTopics[otherAssistnaceIndex];
                        row.helpTopics.splice(otherAssistnaceIndex,1);
                        row.helpTopics.push(otherAssistanceItem);
                    }

                    return (<span>{row.helpTopics && row.helpTopics.length > 0 ? row.helpTopics.join(', '): ''}</span>);
                },
            },
        ];
    }else {
        columns = [
            {
                columnName: 'clientName',
                sortable: false,
                columnHeader: 'Client name',
                decorator: (row: GetSMRDataRow, data: GetSMRDataRow[], rowIndex: number, colIndex: number ) => {
                    if(!!row.meetingSchedule && row.meetingSchedule.length > 0 && row.meetingSchedule !== '  - '){
                        return (<span >{row.clientName}</span>);
                    }else {
                        return (<Link to={`/RSASpecialistMeetingScheduler/${row.meetingRequestId || 0}`}>{row.clientName}</Link>);
                    }
                    
                },
            },
            {
                columnName: 'requestedBy',
                sortable: false,
                columnHeader: 'Requested by',
                decorator:  (row: GetSMRDataRow, data: GetSMRDataRow[], rowIndex: number, colIndex: number ) => {
                    return (<span>{row.requestedBy ? row.requestedBy: 'Unknown'}</span>);
                },
            },
            {
                columnName: 'requestedOn',
                sortable: false,
                columnHeader: 'Requested on',
                decorator: (row: GetSMRDataRow, data: GetSMRDataRow[], rowIndex: number, colIndex: number ) => {
                    return (<span>{moment(row.requestedOn).format('MM/DD/yyyy')}</span>);
                },
            },
            {
                columnName: 'helpTopics',
                sortable: false,
                columnHeader: 'Help topic',
                decorator: (row: GetSMRDataRow, data: GetSMRDataRow[], rowIndex: number, colIndex: number ) => {
                    let otherAssistnaceIndex = row.helpTopics.findIndex((item)=>{
                        return item.toLocaleLowerCase().includes('other');
                    });
                    if(otherAssistnaceIndex !== -1){
                        let otherAssistanceItem = row.helpTopics[otherAssistnaceIndex];
                        row.helpTopics.splice(otherAssistnaceIndex,1);
                        row.helpTopics.push(otherAssistanceItem);
                    }
                    return (<span>{row.helpTopics && row.helpTopics.length > 0 ? row.helpTopics.join(', '): ''}</span>);
                },
            },
            {
                columnName: 'assignedTo',
                sortable: false,
                columnHeader: 'Employment coordinator',
                decorator:  (row: GetSMRDataRow, data: GetSMRDataRow[], rowIndex: number, colIndex: number ) => {
                    return (<span>{row.assignedTo ? row.assignedTo: 'Unassigned'}</span>);
                },
            },
            {
                columnName: 'meetingSchedule',
                sortable: false,
                columnHeader: 'Meeting Schedule',
                decorator: undefined,
            },
        ];
    }

    return [columns, recordsPerPage, totalRecords, pageNum,
        pageSizeOptions, searchRef, searchText, data, isLoading, sortOrderDesc, sortByField,
        isPending, assignedTo, handleAssignedToChange, masterData,
        handlePageSizeChange, handleSearchChange, resetSearch, handlePaginationChange, handleSortChange, handleIsPendingChange,
    ] as const;
}